'use client';

import { FC, useCallback, useMemo } from 'react';
import { RecipeEditorDialog, RecipeEditorDialogConfig } from './RecipeEditorDialog';
import { useBackend } from '@/hooks/useBackend';
import { log } from '@/lib/utils/logger';
import type { RecipeFavoriteDto, RecipeDto } from '@/api/ApiClient';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';

/**
 * Recipe Favorite Editor Dialog
 *
 * Dialog for editing recipe favorites. Uses the general RecipeEditorDialog
 * with recipe favorite-specific save logic.
 *
 * Note: The favorite data should be pre-loaded before opening this dialog.
 */
const OPTION_KEYS: OptionKey[] = ['synthesisTechniques', 'synthesisInstruments'];

export interface RecipeFavoriteEditorDialogProps {
  open: boolean;
  favorite: RecipeFavoriteDto | null;
  onClose: (saved?: RecipeFavoriteDto) => void;
}

export const RecipeFavoriteEditorDialog: FC<RecipeFavoriteEditorDialogProps> = ({ open, favorite, onClose }) => {
  const backend = useBackend();
  const { options } = useSelectOptions(OPTION_KEYS);

  // Resolve technique and instrument IDs to labels
  const techniqueLabel = useMemo(() => {
    if (!favorite?.technique) return '';
    return options.synthesisTechniques?.find((t) => t.id === favorite.technique)?.name || favorite.technique;
  }, [favorite, options.synthesisTechniques]);

  const instrumentLabel = useMemo(() => {
    if (!favorite?.instrument) return '';
    return options.synthesisInstruments?.find((i) => i.id === favorite.instrument)?.name || favorite.instrument;
  }, [favorite, options.synthesisInstruments]);

  // Build info line
  const infoLine = useMemo(() => {
    if (!favorite) return '';
    const parts: string[] = [];
    if (favorite.name) {
      parts.push(`Name: ${favorite.name}`);
    }
    if (techniqueLabel) {
      parts.push(`Technique: ${techniqueLabel}`);
    }
    if (instrumentLabel) {
      parts.push(`Instrument: ${instrumentLabel}`);
    }
    if (favorite.step) {
      parts.push(`Step: ${favorite.step}`);
    }
    if (favorite.template) {
      parts.push(`Template: ${favorite.template}`);
    }
    return parts.join(' • ');
  }, [favorite, techniqueLabel, instrumentLabel]);

  // Handle save
  const handleSave = useCallback(
    async (recipe: RecipeDto) => {
      if (!favorite?.id) {
        throw new Error('Favorite ID is required');
      }

      try {
        const updatedFavorite: RecipeFavoriteDto = {
          ...favorite,
          recipe,
        };
        await backend.recipeFavorite.update(favorite.id, updatedFavorite);
        onClose(updatedFavorite);
      } catch (err: any) {
        log.error('[RecipeFavoriteEditorDialog] Error saving favorite:', err);
        throw err;
      }
    },
    [favorite, backend, onClose]
  );

  // Build dialog config
  const dialogConfig: RecipeEditorDialogConfig = useMemo(
    () => ({
      title: 'Recipe Favorite Details',
      activityType: favorite?.step,
      technique: favorite?.technique,
      instrument: favorite?.instrument,
      recipe: favorite?.recipe,
      recipeTemplate: favorite?.template,
      isReadOnly: false,
      infoLine,
      onSave: handleSave,
      onClose: () => onClose(),
    }),
    [favorite, infoLine, handleSave, onClose]
  );

  // Now we can safely return null if favorite is not set
  // after all the memos, callbacks, etc.!
  if (!favorite) return null;

  return <RecipeEditorDialog open={open} config={dialogConfig} />;
};
